import { useCurrentUser, useIsArticAdmin } from '@artic/api-client';
import { ChangePartnerMenu } from '@artic/ui/change-partner-menu';
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@artic/ui/dropdown-menu';
import {
  LAST_PARTNER_COOKIE,
  readCookie,
  setLastPartnerCookie,
  setPartnerCookie,
} from '@artic/ui/lib/auth-cookies';
import { NavUser as NavUserShell } from '@artic/ui/nav-user';
import { LogoutIcon, Moon02Icon, Settings05Icon, Sun02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { redirectToHub } from '../auth';
import { useTheme } from './use-theme';

/** The partner_key of the "Artic" partner, always pinned first in the switcher. */
const ARTIC_PARTNER_KEY = 'ART';

const COOKIE_OPTIONS = (() => {
  const domain = import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined;
  return domain ? { domain } : {};
})();

export function NavUser() {
  const { user, partners, activePartner } = useCurrentUser();
  const isAdmin = useIsArticAdmin();
  const { theme, setTheme } = useTheme();
  const matches = useRouterState({ select: (state) => state.matches });

  const displayName =
    (user.profile_name ??
      [user.profile_first_name, user.profile_last_name].filter(Boolean).join(' ')) ||
    user.email;

  const isDark = theme === 'dark';

  // Remember the active partner as the "last non-Artic partner viewed" so the
  // switcher can pin it after the admin jumps to Artic and wants to jump back.
  useEffect(() => {
    const active = partners.find((p) => p.partner_key === activePartner);
    if (active && active.partner_key !== ARTIC_PARTNER_KEY) {
      setLastPartnerCookie(active.partner_key, COOKIE_OPTIONS);
    }
  }, [activePartner, partners]);

  const handleSelectPartner = (partnerKey: string) => {
    setPartnerCookie(partnerKey, COOKIE_OPTIONS);
    if (partnerKey !== ARTIC_PARTNER_KEY) {
      setLastPartnerCookie(partnerKey, COOKIE_OPTIONS);
    }
    // A full reload re-reads the cookie so nav, feature flags, and every
    // partner-scoped query reflect the new partner. Detail routes carry an id
    // that belongs to the old partner, so lift those back to the app home;
    // list/index routes are partner-safe and just reload in place.
    const leaf = matches[matches.length - 1];
    const isDetailRoute = leaf ? Object.keys(leaf.params ?? {}).length > 0 : false;
    if (isDetailRoute) {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  };

  return (
    <NavUserShell user={{ name: displayName, email: user.email }}>
      {isAdmin ? (
        <>
          <ChangePartnerMenu
            partners={partners.map((p) => ({
              key: p.partner_key,
              name: p.partner_display_name?.trim() || p.partner_name,
            }))}
            activePartnerKey={activePartner}
            articPartnerKey={ARTIC_PARTNER_KEY}
            lastPartnerKey={readCookie(LAST_PARTNER_COOKIE)}
            onSelect={handleSelectPartner}
          />
          <DropdownMenuSeparator />
        </>
      ) : null}
      <DropdownMenuGroup>
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            setTheme(isDark ? 'light' : 'dark');
          }}
        >
          <HugeiconsIcon icon={isDark ? Sun02Icon : Moon02Icon} strokeWidth={2} />
          {isDark ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
        <DropdownMenuItem>
          <HugeiconsIcon icon={Settings05Icon} strokeWidth={2} />
          Settings
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => redirectToHub()}>
        <HugeiconsIcon icon={LogoutIcon} strokeWidth={2} />
        Log out
      </DropdownMenuItem>
    </NavUserShell>
  );
}
